/**
 * Utilitários compartilhados para componentes AI do editor.
 *
 * Centraliza funções que eram duplicadas entre:
 * - ai-actions.ts
 * - ai-selection-improve.ts
 * - component-library-modal.ts
 * - plugins-ai-components.js
 */

/** Seletores para localizar o iframe do editor TinyMCE */
const EDITOR_IFRAME_SELECTORS = [
    '.editor-container iframe',
    '[component*="page-editor"] iframe',
    '.lexical-wrapper iframe',
];

/**
 * Obtém o CSRF token da meta tag do documento.
 */
export function getCsrfToken(): string {
    return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
}

/**
 * Obtém o page_id da option do page-editor no DOM.
 */
export function getPageId(): number | null {
    const el = document.querySelector('[component*="page-editor"]');
    const raw = el?.getAttribute('option:page-editor:page-id') || null;
    if (raw && raw !== '0') {
        return parseInt(raw, 10);
    }
    return null;
}

/**
 * Escapa HTML para exibição segura (XSS-safe via DOM API).
 */
export function escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}

/**
 * Localiza o iframe do editor TinyMCE no DOM.
 */
export function findEditorIframe(): HTMLIFrameElement | null {
    for (const sel of EDITOR_IFRAME_SELECTORS) {
        const el = document.querySelector(sel) as HTMLIFrameElement;
        if (el && el.contentDocument) return el;
    }
    return null;
}

/**
 * Denormaliza entidades HTML usando a API nativa do browser.
 * Substitui o mapa manual de ~30 entidades por solução completa e segura.
 */
export function denormalizeHtml(html: string): string {
    const txt = document.createElement('textarea');
    txt.innerHTML = html;
    return txt.value;
}

/**
 * Corrige escapes literais (\n, \t) dentro de blocos <pre> e <code>.
 *
 * LLMs frequentemente geram JSON/código com \n literal (os dois caracteres
 * barra invertida + n) em vez de quebras de linha reais. Isso resulta em
 * blocos de código exibindo "{\n  \"token\": ...}" numa linha só.
 *
 * Esta função processa apenas o conteúdo dentro de tags <pre> e <code>,
 * convertendo as sequências de escape em caracteres reais.
 */
export function fixCodeBlockEscapes(html: string): string {
    return html.replace(
        /(<(?:pre|code)\b[^>]*>)([\s\S]*?)(<\/(?:pre|code)>)/gi,
        (_match: string, openTag: string, content: string, closeTag: string) => {
            const fixed = content
                .replace(/\\n/g, '\n')
                .replace(/\\t/g, '\t');
            return openTag + fixed + closeTag;
        }
    );
}

/**
 * Remove wrappers `.code-block-wrapper` gerados pelo componente Chat ao
 * exibir mensagens (header com nome da linguagem + botão "Copiar").
 *
 * Esses wrappers são adicionados APENAS para renderização no chat — eles
 * nunca devem ser persistidos em páginas do BookStack. A função substitui:
 *
 *   <div class="code-block-wrapper">
 *     <div class="code-block-header">...</div>
 *     <pre>...</pre>
 *   </div>
 *
 * de volta para o `<pre>` limpo original.
 */
export function stripChatCodeWrappers(html: string): string {
    if (!html.includes('code-block-wrapper')) return html;
    const div = document.createElement('div');
    div.innerHTML = html;
    div.querySelectorAll<HTMLElement>('.code-block-wrapper').forEach(wrapper => {
        // Remove o header (contém label da linguagem + botão Copiar)
        wrapper.querySelector('.code-block-header')?.remove();
        // Desaninha o <pre> (ou qualquer outro filho restante) para fora do wrapper
        while (wrapper.firstChild) {
            wrapper.parentNode!.insertBefore(wrapper.firstChild, wrapper);
        }
        wrapper.remove();
    });
    return div.innerHTML;
}

/**
 * Extrai texto puro de HTML (strip tags).
 */
export function htmlToPlainText(html: string): string {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
}
